/**
 * SOLID Review
 * S — Strong. One concern: declare what ComponentFrontendInfrastructure supplies — the client's state
 *     and the three server gateways for the Application, the state view for the Framework.
 * O — Good. A new lollipop is one field, assigned from one more part.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One lollipop per port interface.
 * D — Strong. The kernel and the Application's port types; the parts arrive built.
 */
import { ProvidedPort, PortHolder } from '@ml3d/kernel';
import {
  ClientStateContract, ClientRoundServerContract, ClientRoomServerContract, ClientServerWatchContract,
  ClientTabStorageContract, ClientPlayerServerContract, ClientAuthServerContract, ClientLegalServerContract,
  ClientConsentPlatformContract, ClientPaymentServerContract, ClientAdsServerContract, ClientServerHealthContract,
  ClientMediaServerContract,
  type IPortClientState, type IPortClientRoundServer, type IPortClientRoomServer, type IPortClientServerWatch,
  type IPortClientTabStorage, type IPortClientPlayerServer, type IPortClientAuthServer, type IPortClientLegalServer,
  type IPortClientConsentPlatform, type IPortClientPaymentServer, type IPortClientAdsServer, type IPortClientServerHealth,
  type IPortClientMediaServer,
} from '../../application/ports/contracts';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';

/** The objects behind the lollipops, built by `ComponentFrontendInfrastructure.build()`. */
export interface IConfigDepInfrastructureLollipops {
  readonly state: IPortClientState;
  readonly view: IPortClientStateView;
  readonly rounds: IPortClientRoundServer;
  readonly rooms: IPortClientRoomServer;
  readonly watch: IPortClientServerWatch;
  readonly tabStorage: IPortClientTabStorage;
  readonly players: IPortClientPlayerServer;
  readonly auth: IPortClientAuthServer;
  readonly legal: IPortClientLegalServer;
  readonly consent: IPortClientConsentPlatform;
  readonly payments: IPortClientPaymentServer;
  readonly ads: IPortClientAdsServer;
  readonly health: IPortClientServerHealth;
  readonly media: IPortClientMediaServer;
}

/**
 * What ComponentFrontendInfrastructure supplies (frontend strict-ports plan, FE1d, 2026-10-03) — the
 * twin of the backend's `ComponentInfrastructureProvidedPorts`. Built in the component's `build()`,
 * because the objects behind it wrap adapters that read the wire sockets, connected one phase earlier.
 *
 * Thirteen contracted lollipops for the Application (three since FE2: the tab's storage, players, the
 * account; the legal server in FE4a, the consent platform in FE4b, the payment server in FE4c, the ads
 * server in FE4d, the server's health in FE5a, the media server in FE5b), and
 * `ClientStateView` for the Framework — UNCONTRACTED,
 * on `ports:check`'s VIEW_BY_REFERENCE list: React's `useSyncExternalStore` needs the store's own
 * object back, and a parse would hand it a new one each time.
 *
 * Fields assigned in the constructor body (`useDefineForClassFields`); walked because this class extends
 * `PortHolder`.
 */
export class ComponentFrontendInfrastructureProvidedPorts extends PortHolder {
  readonly clientStatePort: ProvidedPort<IPortClientState>;
  readonly clientStateViewPort: ProvidedPort<IPortClientStateView>;
  readonly clientRoundServerPort: ProvidedPort<IPortClientRoundServer>;
  readonly clientRoomServerPort: ProvidedPort<IPortClientRoomServer>;
  readonly clientServerWatchPort: ProvidedPort<IPortClientServerWatch>;
  readonly clientTabStoragePort: ProvidedPort<IPortClientTabStorage>;
  readonly clientPlayerServerPort: ProvidedPort<IPortClientPlayerServer>;
  readonly clientAuthServerPort: ProvidedPort<IPortClientAuthServer>;
  /** The legal documents and the account's legal acts (FE4a). */
  readonly clientLegalServerPort: ProvidedPort<IPortClientLegalServer>;
  /** The consent management platform (FE4b). */
  readonly clientConsentPlatformPort: ProvidedPort<IPortClientConsentPlatform>;
  /** The account's checkout and balance (FE4c). */
  readonly clientPaymentServerPort: ProvidedPort<IPortClientPaymentServer>;
  /** The ad provider the server chose for a player (FE4d). */
  readonly clientAdsServerPort: ProvidedPort<IPortClientAdsServer>;
  /** The server's /health (FE5a). */
  readonly clientServerHealthPort: ProvidedPort<IPortClientServerHealth>;
  /** The video chat's signaling and admin reads (FE5b). */
  readonly clientMediaServerPort: ProvidedPort<IPortClientMediaServer>;

  constructor(parts: IConfigDepInfrastructureLollipops) {
    super();
    this.clientStatePort = new ProvidedPort<IPortClientState>('ClientState', parts.state, ClientStateContract);
    this.clientStateViewPort = new ProvidedPort<IPortClientStateView>('ClientStateView', parts.view);
    this.clientRoundServerPort = new ProvidedPort<IPortClientRoundServer>('ClientRoundServer', parts.rounds, ClientRoundServerContract);
    this.clientRoomServerPort = new ProvidedPort<IPortClientRoomServer>('ClientRoomServer', parts.rooms, ClientRoomServerContract);
    this.clientServerWatchPort = new ProvidedPort<IPortClientServerWatch>('ClientServerWatch', parts.watch, ClientServerWatchContract);
    this.clientTabStoragePort = new ProvidedPort<IPortClientTabStorage>('ClientTabStorage', parts.tabStorage, ClientTabStorageContract);
    this.clientPlayerServerPort = new ProvidedPort<IPortClientPlayerServer>('ClientPlayerServer', parts.players, ClientPlayerServerContract);
    this.clientAuthServerPort = new ProvidedPort<IPortClientAuthServer>('ClientAuthServer', parts.auth, ClientAuthServerContract);
    this.clientLegalServerPort = new ProvidedPort<IPortClientLegalServer>('ClientLegalServer', parts.legal, ClientLegalServerContract);
    this.clientConsentPlatformPort = new ProvidedPort<IPortClientConsentPlatform>('ClientConsentPlatform', parts.consent, ClientConsentPlatformContract);
    this.clientPaymentServerPort = new ProvidedPort<IPortClientPaymentServer>('ClientPaymentServer', parts.payments, ClientPaymentServerContract);
    this.clientAdsServerPort = new ProvidedPort<IPortClientAdsServer>('ClientAdsServer', parts.ads, ClientAdsServerContract);
    this.clientServerHealthPort = new ProvidedPort<IPortClientServerHealth>('ClientServerHealth', parts.health, ClientServerHealthContract);
    this.clientMediaServerPort = new ProvidedPort<IPortClientMediaServer>('ClientMediaServer', parts.media, ClientMediaServerContract);
  }
}
